import { pick } from 'lodash-es';
import { ArchiveIcon, KIcon, PlayerIcon, SaveIcon, TimeIcon } from '@karnott/icons';
import { TASK_STATUS, TaskStatus } from '../constants/taskConstants';
import { I18n, LocaleKey } from '../i18n';
import { TaskArticle } from '../slices/tasks';
import { ApiWorkOrder, CreateWorkOrderPayload, WorkOrder, WorkOrderArticleGroup } from '../slices/workOrders';

function getArticleValuesFromArticleGroup(group: WorkOrderArticleGroup) {
  return {
    client_cluster_id: group.client_cluster_id,
    goal_value: group.goal_value,
    worked_value: group.worked_value,
    computed_worked_value: group.computed_worked_value,
    range_value: group.range_value,
  };
}

export function transformApiWorkOrder(apiWorkOrder: ApiWorkOrder): WorkOrder {
  const firstTask = apiWorkOrder.tasks?.[0];
  return {
    is_work_order: true,
    id: apiWorkOrder.id,
    task_type_id: apiWorkOrder.task_type_id,
    type: firstTask?.type,
    authorizations: apiWorkOrder.authorizations,
    articles:
      apiWorkOrder.article_groups?.reduce<TaskArticle[]>((acc, group) => {
        const currentArticle = acc.find((article) => article.id === group.id);
        if (currentArticle) {
          currentArticle.goal_value = (currentArticle.goal_value || 0) + (group.goal_value || 0);
          currentArticle.worked_value = (currentArticle.worked_value || 0) + (group.worked_value || 0);
          currentArticle.values ??= [];
          currentArticle.values.push(getArticleValuesFromArticleGroup(group));
          return acc;
        }
        return [
          ...acc,
          {
            ...group,
            position: group.position,
            goal_value: group.goal_value,
            worked_value: group.worked_value,
            computed_worked_value: group.computed_worked_value,
            values: [getArticleValuesFromArticleGroup(group)],
          },
        ];
      }, []) || [],
    assigned_to_ids: apiWorkOrder.drivers?.map((driver) => driver.user_id) || [],
    assigned_users: apiWorkOrder.drivers || [],
    crop_range_id: apiWorkOrder.crop_range_id,
    client_cluster_ids: apiWorkOrder.client_cluster_ids || [],
    completed_tasks: apiWorkOrder.completed_tasks,
    created_at: apiWorkOrder.created_at,
    device_session_ids: apiWorkOrder.device_session_ids,
    equipment_instance_ids: apiWorkOrder.equipment_instances?.map((equipment) => equipment.id) || [],
    from_date: apiWorkOrder.from_ts,
    to_date: apiWorkOrder.to_ts,
    metadata: apiWorkOrder.metadata || [],
    name: apiWorkOrder.name,
    observation_ids: apiWorkOrder.observations?.map((observation) => observation.id) || [],
    observations: apiWorkOrder.observations,
    progress: apiWorkOrder.progress,
    progress_percent: apiWorkOrder.progress_percent,
    status: apiWorkOrder.status,
    owner_cluster_id: apiWorkOrder.owner_cluster_id,
    owner_name: apiWorkOrder.owner_cluster_name,
    parcels: apiWorkOrder.parcels || [],
    pdf_config: apiWorkOrder.pdf_config,
    product_usages: apiWorkOrder.product_usages,
    tasks: apiWorkOrder.tasks,
    track: apiWorkOrder.track,
    total_dose: apiWorkOrder.total_dose,
    total_quantity: apiWorkOrder.total_quantity,
    water_data: apiWorkOrder.water_data,
  };
}

export function transformWorkOrderToWorkOrderPayload(workOrder: Partial<WorkOrder>): CreateWorkOrderPayload {
  return {
    article_groups: (workOrder.articles || [])
      .map((a) =>
        (a.values || []).map((values) => ({
          article_id: a.id,
          client_cluster_id: values.client_cluster_id,
          goal_value: values.goal_value,
          worked_value: values.worked_value,
          range_value: values.range_value,
          position: a.position,
        })),
      )
      .flat(),
    client_cluster_ids: workOrder.client_cluster_ids || [],
    from_ts: workOrder.from_date,
    owner_cluster_id: workOrder.owner_cluster_id!,
    to_ts: workOrder.to_date,
    parcels: workOrder.parcels,
    status: workOrder.status!,
    task_payload: {
      ...pick(workOrder, [
        'assigned_to_ids',
        'crop_range_id',
        'device_session_ids',
        'equipment_instance_ids',
        'name',
        'product_usages',
        'task_type_id',
        'total_dose',
      ]),
      metadata: (workOrder.metadata || []).map((m) => ({
        task_metadata_id: m.id!,
        responses: m.responses,
      })),
    },
  };
}

const TASK_STATUS_ORDER = [
  TASK_STATUS.TO_DO,
  TASK_STATUS.IN_PROGRESS,
  TASK_STATUS.DRAFT,
  TASK_STATUS.DONE,
  TASK_STATUS.BILLED,
  TASK_STATUS.ARCHIVED,
];

export function isUnplannedWorkOrder(workOrder: WorkOrder) {
  return !workOrder.from_date && !workOrder.to_date;
}

export function getStatusTransitionLabel(currentStatus: TaskStatus | undefined, targetStatus: TaskStatus) {
  if (!currentStatus || currentStatus === targetStatus) {
    return I18n.t(`Tasks.status_${targetStatus}`);
  }
  if (currentStatus === TASK_STATUS.DRAFT && targetStatus === TASK_STATUS.DONE) {
    return I18n.t(`Tasks.status_draft_to_done`);
  }
  const currentIndex = TASK_STATUS_ORDER.indexOf(currentStatus);
  const targetIndex = TASK_STATUS_ORDER.indexOf(targetStatus);
  if (currentIndex === -1 || targetIndex === -1) {
    return I18n.t(`Tasks.status_${targetStatus}`);
  }
  if (currentIndex < targetIndex) {
    return I18n.t(`Tasks.status_to_${targetStatus}` as LocaleKey);
  }
  return I18n.t(`Tasks.status_back_to_${targetStatus}` as LocaleKey);
}

export function getStatusTransitionIcon(
  currentStatus: TaskStatus | undefined,
  targetStatus: TaskStatus,
): KIcon | undefined {
  if (!currentStatus) {
    return undefined;
  }
  const currentIndex = TASK_STATUS_ORDER.indexOf(currentStatus);
  const targetIndex = TASK_STATUS_ORDER.indexOf(targetStatus);
  if (currentIndex === -1 || targetIndex === -1) {
    return undefined;
  }
  if (currentIndex < targetIndex) {
    switch (targetStatus) {
      case TASK_STATUS.IN_PROGRESS:
        return PlayerIcon;
      case TASK_STATUS.DRAFT:
      case TASK_STATUS.DONE:
      case TASK_STATUS.BILLED:
        return SaveIcon;
      case TASK_STATUS.ARCHIVED:
        return ArchiveIcon;
      default:
        return undefined;
    }
  }
  switch (targetStatus) {
    case TASK_STATUS.TO_DO:
      return TimeIcon;
    case TASK_STATUS.IN_PROGRESS:
    case TASK_STATUS.DRAFT:
    case TASK_STATUS.DONE:
    case TASK_STATUS.BILLED:
      return SaveIcon;
    default:
      return undefined;
  }
}

export function getNextWorkOrderStatuses(currentStatus: TaskStatus | undefined): TaskStatus[] {
  if (!currentStatus) {
    return [];
  }
  switch (currentStatus) {
    case TASK_STATUS.TO_DO:
      return [TASK_STATUS.IN_PROGRESS, TASK_STATUS.DRAFT];
    case TASK_STATUS.IN_PROGRESS:
      return [TASK_STATUS.DRAFT];
    case TASK_STATUS.DRAFT:
      return [TASK_STATUS.DONE];
    case TASK_STATUS.DONE:
      return [TASK_STATUS.ARCHIVED];
    case TASK_STATUS.ARCHIVED:
      return [TASK_STATUS.DONE];
    default:
      return [];
  }
}
